import Link from '@/components/compat/Link';
import { useCms } from '@/components/painel/cms/cms-context';
import { BrandPeekSmilies } from '@/components/help-center/BrandPeekSmilies';
import { SiteBrandLogo } from '@/components/help-center/SiteBrandLogo';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { normalizeFooter } from '@/lib/site-defaults';
import { XAT_AJUDA_VERSION } from '@/lib/site-version';
import { cn } from '@/lib/utils';
import type { SiteFooterLink } from '@/lib/cms-types';

export { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';

/** Badges oficiais (Apple / Google brand guidelines) */
const BADGE_APP_STORE =
  'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/pt-br?size=250x83';
const BADGE_GOOGLE_PLAY =
  'https://play.google.com/intl/en_us/badges/static/images/badges/pt-br_badge_web_generic.png';

const footerLinkClassName =
  'inline-flex items-center whitespace-nowrap rounded-lg px-3 py-1.5 -mx-3 font-poppins text-sm text-blue-100 transition-all duration-200 hover:bg-white/20 hover:text-white';

function isInternalHref(href: string) {
  return href.startsWith('/') && !href.startsWith('//');
}

/** Badge de versão aponta sempre para o changelog do xat Ajuda (não o do xat). */
function resolveAjudaChangelogHref(href: string): string {
  const raw = href.trim() || '/changelog?produto=ajuda';
  if (!isInternalHref(raw)) return raw;

  try {
    const url = new URL(raw, 'https://xatajuda.local');
    if (url.pathname.replace(/\/$/, '') !== '/changelog') return raw;
    if (url.searchParams.get('produto') !== 'ajuda') {
      url.searchParams.set('produto', 'ajuda');
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return '/changelog?produto=ajuda';
  }
}

function FooterNavLink({ link }: { link: SiteFooterLink }) {
  if (isInternalHref(link.href)) {
    return (
      <Link href={link.href} className={footerLinkClassName}>
        {link.label}
      </Link>
    );
  }

  return (
    <a href={link.href || '#'} className={footerLinkClassName}>
      {link.label}
    </a>
  );
}

export default function Footer() {
  const { site, siteReady } = useCms();

  if (!siteReady) {
    return (
      <footer
        className="bg-gradient-to-r from-[#030023] to-[#000b6a]"
        aria-busy="true"
        aria-label="Rodapé a carregar"
      >
        <div className="mx-auto min-h-[22rem] w-full max-w-7xl px-6 py-16" />
      </footer>
    );
  }

  const footer = normalizeFooter(site.footer);
  const changelogVersion =
    footer.changelog.version.trim().length > 0
      ? footer.changelog.version.trim()
      : XAT_AJUDA_VERSION;
  const changelogHref = resolveAjudaChangelogHref(footer.changelog.href);
  const changelogPrefix =
    footer.changelog.prefix.trim().length > 0 ? footer.changelog.prefix.trim() : 'xatAjuda.com';
  const disclaimer = footer.disclaimer.trim();

  const versionBadgeClass = cn(
    'inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-white/[0.06] px-3.5 py-1.5',
    'font-poppins text-xs font-semibold text-sky-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]',
    'backdrop-blur-sm transition-all duration-200',
    'hover:border-sky-300/45 hover:bg-white/10 hover:text-white'
  );

  return (
    <footer className="bg-gradient-to-r from-[#030023] to-[#000b6a]">
      <div className="mx-auto w-full max-w-7xl px-6 py-16">
        <div className="mb-12 grid gap-10 lg:grid-cols-[minmax(15rem,17.5rem)_minmax(0,1fr)_auto] lg:items-start lg:gap-x-14 lg:gap-y-8">
          <div className="flex min-w-0 flex-col gap-3">
            <div className="relative w-fit overflow-visible pt-10">
              <BrandPeekSmilies
                className="absolute bottom-[calc(100%-0.35rem)] left-1/2 z-10 -translate-x-[66%]"
                scale={0.88}
                codes={footer.peekSmilies}
              />
              <SiteBrandLogo
                name={footer.brandName}
                className="relative z-0 text-3xl md:text-[2rem]"
              />
            </div>
            <p className="max-w-[17rem] text-sm leading-relaxed text-blue-100">
              {footer.brandDescription}
            </p>
          </div>

          <div className="flex w-full flex-wrap gap-x-10 gap-y-8 sm:gap-x-12 lg:justify-between lg:gap-x-8">
            {footer.columns.map((column) => (
              <div key={column.id} className="shrink-0">
                <h3 className="mb-4 font-poppins text-sm font-semibold text-white">{column.title}</h3>
                <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                  {column.links.map((link, index) => (
                    <li key={`${column.id}-${index}-${link.label}`}>
                      <FooterNavLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2.5 sm:items-start lg:items-end">
            <h3 className="mb-0.5 font-poppins text-sm font-semibold text-white lg:text-right">
              {footer.appsSectionTitle}
            </h3>
            <a
              href={footer.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-opacity hover:opacity-90 focus-visible:outline focus-visible:ring-2 focus-visible:ring-white/40"
              aria-label={footer.appStoreAriaLabel}
              title={footer.appStoreAriaLabel}
            >
              <img
                src={BADGE_APP_STORE}
                alt={footer.appStoreAriaLabel}
                width={140}
                height={46}
                className="h-[46px] w-auto max-w-[140px] object-contain"
              />
            </a>
            <a
              href={footer.googlePlayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-opacity hover:opacity-90 focus-visible:outline focus-visible:ring-2 focus-visible:ring-white/40"
              aria-label={footer.googlePlayAriaLabel}
              title={footer.googlePlayAriaLabel}
            >
              <img
                src={BADGE_GOOGLE_PLAY}
                alt={footer.googlePlayAriaLabel}
                width={155}
                height={60}
                className="h-[60px] w-auto max-w-[155px] object-contain"
              />
            </a>
          </div>
        </div>

        {/* Separador = Powerpédia (horizontal: linhas + cristal) */}
        <PortalCrystalSeparator className="mb-8" />

        <div className="pt-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <p className="font-poppins text-sm text-blue-100">{footer.copyright}</p>
              {isInternalHref(changelogHref) ? (
                <Link href={changelogHref} className={versionBadgeClass}>
                  <span className="text-[11px] font-semibold tracking-wide text-sky-200/90">
                    {changelogPrefix}
                  </span>
                  <span className="h-3 w-px bg-white/20" aria-hidden />
                  <span className="tabular-nums text-sky-100">v{changelogVersion}</span>
                </Link>
              ) : (
                <a href={changelogHref} className={versionBadgeClass}>
                  <span className="text-[11px] font-semibold tracking-wide text-sky-200/90">
                    {changelogPrefix}
                  </span>
                  <span className="h-3 w-px bg-white/20" aria-hidden />
                  <span className="tabular-nums text-sky-100">v{changelogVersion}</span>
                </a>
              )}
            </div>

            {disclaimer ? (
              <p className="max-w-3xl font-inter text-[11px] leading-relaxed text-blue-100/70 md:text-xs md:leading-relaxed">
                {disclaimer}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}
